// Plan editor — the pure logic behind the full-plan form in `_authed.plans.tsx`.
//
// Colocated with the route and prefixed `-` so the TanStack Router plugin
// ignores it (same convention as `-promotions.ts` / `-block-fields.tsx`). It
// lives outside the .tsx so it can be unit-tested under the admin's
// `environment: 'node'` vitest config with no DOM and no JSX.
//
// ─────────────────────────────────────────────────────────────────────────────
// 🔴 THE ONE IDEA THIS FILE EXISTS TO PROTECT: null ≠ 0 ≠ absent.
//
// `plans.max_kids` / `max_teachers` / `max_students` are nullable integers
// where **null means UNLIMITED**. Three distinct states have to survive the
// round trip through an HTML input, which only ever holds a string:
//
//     null       → "Unlimited"          — no cap at all
//     a number   → "Limited to N"       — a real cap, N ≥ 1
//     absent     → "leave unchanged"    — never sent on a PATCH
//
// An empty text box is the trap. It reads naturally as "no limit", but it is
// also what you get halfway through typing, and `Number('')` is 0 — which the
// API rejects (`.positive()`) precisely because a plan capped at zero kids
// would be unusable while looking valid. So the editor models the limit as an
// explicit MODE plus a value, never as "empty string means unlimited", and
// `parseLimit` below refuses to guess.
//
// The same care applies to the diff: `buildPatch` sends a key only when it
// actually changed, so a PATCH that touches the tagline cannot silently
// rewrite the limits, and an unlimited limit is transmitted as a literal
// `null` rather than by omitting the key.
// ─────────────────────────────────────────────────────────────────────────────
//
// 🔴 Trial config mirrors `adminPlanUpsertInput` in packages/zod-schemas:
// trialDays 0–365, and `trialRequiresCard` is refused when trialDays is 0.
// Change one, change both — `-plan-editor.test.ts` asserts the pair.

import type { AdminPlan, PlanUpdateInput } from '~/lib/billing';

/** A limit is either uncapped or capped at a positive integer. */
export type LimitMode = 'unlimited' | 'limited';

/** Which limits a plan audience actually uses. */
export const FAMILY_LIMITS = ['maxKids'] as const;
export const SCHOOL_LIMITS = ['maxTeachers', 'maxStudents'] as const;

export type LimitField = 'maxKids' | 'maxTeachers' | 'maxStudents';

export const LIMIT_LABEL: Record<LimitField, string> = {
  maxKids: 'Kids',
  maxTeachers: 'Teachers',
  maxStudents: 'Students',
};

/**
 * Limits are shown per audience, but ALL THREE are still editable when a plan
 * carries a stale value for one that its audience does not use — otherwise a
 * school plan with a leftover `max_kids` would have an invisible, unfixable
 * field. Returns the audience's own limits first, then any stray non-null one.
 */
export function visibleLimits(audience: 'family' | 'school', plan?: AdminPlan): LimitField[] {
  const own: LimitField[] = audience === 'family' ? [...FAMILY_LIMITS] : [...SCHOOL_LIMITS];
  if (!plan) return own;
  const strays = (['maxKids', 'maxTeachers', 'maxStudents'] as LimitField[]).filter(
    (f) => !own.includes(f) && plan[f] != null,
  );
  return [...own, ...strays];
}

/** Exactly the form's state. Strings, because that is what inputs hold. */
export interface PlanDraft {
  slug: string;
  audience: 'family' | 'school';
  tier: string;
  nameKey: string;
  taglineKey: string;
  /** One row per feature. Blank rows are dropped on save, never sent. */
  features: string[];
  isHighlighted: boolean;
  status: 'active' | 'archived';

  maxKidsMode: LimitMode;
  maxKids: string;
  maxTeachersMode: LimitMode;
  maxTeachers: string;
  maxStudentsMode: LimitMode;
  maxStudents: string;

  trialDays: string;
  trialRequiresCard: boolean;
}

export type PlanField =
  | 'slug'
  | 'tier'
  | 'nameKey'
  | 'taglineKey'
  | 'features'
  | 'maxKids'
  | 'maxTeachers'
  | 'maxStudents'
  | 'trialDays'
  | 'trialRequiresCard';

export interface PlanValidation {
  ok: boolean;
  errors: Partial<Record<PlanField, string>>;
}

const MODE_KEY = {
  maxKids: 'maxKidsMode',
  maxTeachers: 'maxTeachersMode',
  maxStudents: 'maxStudentsMode',
} as const satisfies Record<LimitField, keyof PlanDraft>;

/** The mode + value pair for one limit, read off a draft. */
export function limitOf(draft: PlanDraft, field: LimitField): { mode: LimitMode; value: string } {
  return { mode: draft[MODE_KEY[field]], value: draft[field] };
}

/** Set one limit's mode, clearing the number when switching to unlimited. */
export function setLimitMode(draft: PlanDraft, field: LimitField, mode: LimitMode): PlanDraft {
  return {
    ...draft,
    [MODE_KEY[field]]: mode,
    // Clearing on the way to `unlimited` keeps the visible state honest: a
    // greyed-out "5" beside a selected "Unlimited" radio is a lie about what
    // will be saved.
    [field]: mode === 'unlimited' ? '' : draft[field],
  };
}

/** A blank draft for the create form. */
export function emptyDraft(audience: 'family' | 'school' = 'family'): PlanDraft {
  return {
    slug: '',
    audience,
    tier: '',
    nameKey: '',
    taglineKey: '',
    features: [''],
    isHighlighted: false,
    status: 'active',
    // A NEW plan starts unlimited-free rather than capped: the API stores null
    // by default, and starting in `limited` with an empty box would render an
    // error the operator never asked for.
    maxKidsMode: 'unlimited',
    maxKids: '',
    maxTeachersMode: 'unlimited',
    maxTeachers: '',
    maxStudentsMode: 'unlimited',
    maxStudents: '',
    trialDays: '0',
    trialRequiresCard: false,
  };
}

/** Load an existing plan into form state. The inverse of `buildPatch`. */
export function draftFromPlan(plan: AdminPlan): PlanDraft {
  const limit = (v: number | null): { mode: LimitMode; value: string } =>
    v == null ? { mode: 'unlimited', value: '' } : { mode: 'limited', value: String(v) };
  const kids = limit(plan.maxKids);
  const teachers = limit(plan.maxTeachers);
  const students = limit(plan.maxStudents);
  return {
    slug: plan.slug,
    audience: plan.audience,
    tier: plan.tier,
    nameKey: plan.nameKey,
    taglineKey: plan.taglineKey ?? '',
    // Always leave one empty row so the editor has somewhere to type without
    // clicking "Add" first.
    features: plan.features.length > 0 ? [...plan.features] : [''],
    isHighlighted: plan.isHighlighted,
    status: plan.status === 'archived' ? 'archived' : 'active',
    maxKidsMode: kids.mode,
    maxKids: kids.value,
    maxTeachersMode: teachers.mode,
    maxTeachers: teachers.value,
    maxStudentsMode: students.mode,
    maxStudents: students.value,
    trialDays: String(plan.trialDays),
    trialRequiresCard: plan.trialRequiresCard,
  };
}

/**
 * Parse one limit into what the API should receive.
 *
 * Returns `null` for unlimited, a positive integer for a cap, or the string
 * `'invalid'` when the operator's input cannot be either. It does NOT fall
 * back to null on bad input: quietly turning a typo into "unlimited" is how a
 * paid tier ends up uncapped.
 */
export function parseLimit(mode: LimitMode, raw: string): number | null | 'invalid' {
  if (mode === 'unlimited') return null;
  const trimmed = raw.trim();
  if (trimmed === '') return 'invalid';
  if (!/^\d+$/.test(trimmed)) return 'invalid';
  const n = Number(trimmed);
  // 0 is rejected, not coerced: `.positive()` on the API refuses it too, and a
  // zero cap is never what anyone means.
  if (!Number.isSafeInteger(n) || n < 1) return 'invalid';
  return n;
}

/** Trim, drop blanks, and de-duplicate the feature rows. */
export function cleanFeatures(rows: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of rows) {
    const value = row.trim();
    if (value === '') continue;
    // Case-sensitive de-dupe: "Live tutoring" and "live tutoring" are the same
    // bullet to a reader, but only the exact repeat is unambiguously a mistake.
    if (seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

const SLUG_RE = /^[a-z][a-z0-9-]{1,58}$/;
const MAX_FEATURES = 30;
const MAX_TRIAL_DAYS = 365;

/**
 * Mirror of `adminPlanUpsertInput` (packages/zod-schemas/src/billing.ts) over
 * PARTIAL, half-typed form state.
 *
 * Same reasoning as `-promotions.ts`: Zod validates a COMPLETE input, the form
 * needs per-field errors on a draft mid-keystroke. `-plan-editor.test.ts` runs
 * a fixture table through BOTH and asserts they agree on accept/reject.
 */
export function validate(draft: PlanDraft): PlanValidation {
  const errors: Partial<Record<PlanField, string>> = {};

  const slug = draft.slug.trim();
  if (slug === '') errors.slug = 'Required.';
  else if (!SLUG_RE.test(slug))
    errors.slug = 'Lowercase letters, digits and hyphens; 2–59 characters, starting with a letter.';

  if (draft.tier.trim() === '') errors.tier = 'Required.';
  else if (draft.tier.trim().length > 40) errors.tier = 'Max 40 characters.';

  const nameKey = draft.nameKey.trim();
  if (nameKey === '') errors.nameKey = 'Required.';
  else if (nameKey.length > 240) errors.nameKey = 'Max 240 characters.';

  if (draft.taglineKey.trim().length > 240) errors.taglineKey = 'Max 240 characters.';

  const features = cleanFeatures(draft.features);
  if (features.length > MAX_FEATURES) errors.features = `Max ${MAX_FEATURES} features.`;

  for (const field of ['maxKids', 'maxTeachers', 'maxStudents'] as const) {
    const { mode, value } = limitOf(draft, field);
    if (parseLimit(mode, value) === 'invalid') {
      errors[field] = 'Enter a whole number of 1 or more, or choose Unlimited.';
    }
  }

  const days = draft.trialDays.trim();
  let parsedDays: number | null = null;
  if (days === '' || !/^\d+$/.test(days)) {
    errors.trialDays = 'Enter a whole number of days (0 for no trial).';
  } else {
    parsedDays = Number(days);
    if (!Number.isSafeInteger(parsedDays) || parsedDays > MAX_TRIAL_DAYS) {
      errors.trialDays = `Max ${MAX_TRIAL_DAYS} days.`;
      parsedDays = null;
    }
  }

  // The cross-field rule the API enforces too. A card requirement on a
  // zero-day trial is not a harmless no-op — it is a requirement attached to
  // a trial that does not exist.
  if (draft.trialRequiresCard && parsedDays === 0) {
    errors.trialRequiresCard = 'Requires a trial: set trial days above 0.';
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

/** The complete body for `POST /admin/plans`. Call only when `validate().ok`. */
export function buildCreateInput(draft: PlanDraft): PlanUpdateInput {
  const limit = (field: LimitField): number | null => {
    const { mode, value } = limitOf(draft, field);
    const parsed = parseLimit(mode, value);
    // Unreachable behind `validate().ok`; null is the safe read either way.
    return parsed === 'invalid' ? null : parsed;
  };
  const tagline = draft.taglineKey.trim();
  return {
    slug: draft.slug.trim(),
    audience: draft.audience,
    tier: draft.tier.trim(),
    nameKey: draft.nameKey.trim(),
    taglineKey: tagline === '' ? null : tagline,
    features: cleanFeatures(draft.features),
    isHighlighted: draft.isHighlighted,
    maxKids: limit('maxKids'),
    maxTeachers: limit('maxTeachers'),
    maxStudents: limit('maxStudents'),
    trialDays: Number(draft.trialDays.trim()),
    trialRequiresCard: draft.trialRequiresCard,
    status: draft.status,
  };
}

/**
 * The MINIMAL `PATCH /admin/plans/:id` body: only keys whose value differs
 * from the stored plan.
 *
 * 🔴 Why a diff rather than sending everything. The API treats an absent key
 * as "leave unchanged", so a full-body PATCH makes every save a write to every
 * column — including ones another operator changed in a different tab between
 * this page loading and this save. A diff keeps a tagline edit from stamping
 * stale limits back over a colleague's work.
 *
 * 🔴 `null` is a VALUE here, not an omission. Switching a limit to Unlimited
 * emits `maxKids: null`; omitting the key would leave the old cap in place.
 */
export function buildPatch(draft: PlanDraft, plan: AdminPlan): Partial<PlanUpdateInput> {
  const patch: Partial<PlanUpdateInput> = {};
  const next = buildCreateInput(draft);

  if (next.slug !== plan.slug) patch.slug = next.slug;
  if (next.audience !== plan.audience) patch.audience = next.audience;
  if (next.tier !== plan.tier) patch.tier = next.tier;
  if (next.nameKey !== plan.nameKey) patch.nameKey = next.nameKey;
  if (next.taglineKey !== plan.taglineKey) patch.taglineKey = next.taglineKey;
  if (!sameFeatures(next.features, plan.features)) patch.features = next.features;
  if (next.isHighlighted !== plan.isHighlighted) patch.isHighlighted = next.isHighlighted;
  // `!==` compares null to null correctly, so an unchanged unlimited limit is
  // NOT sent, while unlimited→5 and 5→unlimited both are.
  if (next.maxKids !== plan.maxKids) patch.maxKids = next.maxKids;
  if (next.maxTeachers !== plan.maxTeachers) patch.maxTeachers = next.maxTeachers;
  if (next.maxStudents !== plan.maxStudents) patch.maxStudents = next.maxStudents;
  if (next.trialDays !== plan.trialDays) patch.trialDays = next.trialDays;
  if (next.trialRequiresCard !== plan.trialRequiresCard)
    patch.trialRequiresCard = next.trialRequiresCard;
  if (next.status !== plan.status) patch.status = next.status;

  return patch;
}

/** Order-sensitive: feature bullets are rendered in order, so a reorder is a change. */
function sameFeatures(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** True when the draft would send nothing — used to disable Save. */
export function isUnchanged(draft: PlanDraft, plan: AdminPlan): boolean {
  return Object.keys(buildPatch(draft, plan)).length === 0;
}

/** "Unlimited" / "5 kids" — the read-only summary shown on the plan row. */
export function limitSummary(plan: AdminPlan): string {
  const parts: string[] = [];
  for (const field of visibleLimits(plan.audience, plan)) {
    const value = plan[field];
    parts.push(`${value == null ? 'Unlimited' : value} ${LIMIT_LABEL[field].toLowerCase()}`);
  }
  return parts.join(' · ');
}

/** "14-day trial, card required" / "No trial". */
export function trialSummary(plan: Pick<AdminPlan, 'trialDays' | 'trialRequiresCard'>): string {
  if (plan.trialDays <= 0) return 'No trial';
  const days = `${plan.trialDays}-day trial`;
  return plan.trialRequiresCard ? `${days}, card required` : `${days}, no card`;
}
